import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import { DefaultValues, useForm } from 'react-hook-form';
import { DataGrid } from '../MajorListSummaryPage';
import {
	useGetSingleStatusQuery,
	useUpdateSingleStatusMutation,
} from '@/api/exercise/majorApiSlice';
import { AdvanceSeriesSinglePageResDTO } from '@/api/DTO/exercise/major.interface';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';

interface Props {}

export default function SetSingleStatusDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const { majorId } = useParams();
	const [updateSingleStatus] = useUpdateSingleStatusMutation();

	const statusList = [
		{ id: OnoffType.off, name: t('day21.off') },
		{ id: OnoffType.on, name: t('day21.on') },
	];

	const exerciseTypeList = [
		{ id: 1, name: t('day21.audio') },
		{ id: 2, name: t('day21.video') },
	];
	const canList = [
		{ id: true, name: t('day21.can') },
		{ id: false, name: t('day21.cannot') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetSingleStatusQuery(majorId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		const data: any[] = Object.entries(methods.getValues()).map(
			([key, value]) => ({
				singleId: key,
				published: value == 1 ? true : false,
			})
		);
		const response = await updateSingleStatus({
			singlePublishDTOList: data,
			id: majorId || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateExerciseStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'serialNumber',
			headerName: t('marjorExercise.index'),
			flex: 15,
		},
		{
			field: 'titleZh',
			headerName: t('day21.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('day21.exerciseType'),
			flex: 15,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'canCountTime',
			headerName: t('day21.canCountTime'),
			flex: 20,
			renderCell: (params) => (
				<>{showLabel(params.row.canCountTime, canList)}</>
			),
		},
		{
			field: 'canCollectPoints',
			headerName: t('day21.canCollectPoints'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCollectPoints, canList)}</>
			),
		},
		{
			field: 'reward',
			headerName: t('day21.reward'),
			flex: 25,
		},
		{
			field: 'status',
			headerName: t('day21.exerciseStatus'),
			flex: 20,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, statusList)}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.onoff'),
			flex: 40,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.singleId}
					row
					options={statusList}
				/>
			),
		},
	];

	const transformResponse = useCallback(
		(response?: AdvanceSeriesSinglePageResDTO[]) => {
			if (!response) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					id: item.singleId,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});

				methods.setValue(
					item.singleId,
					item.published ? OnoffType.on : OnoffType.off
				);
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('day21.onoff')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('day21.onoff')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.singleId}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
